import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Wallet, Plus, Trash2, Pencil, X, RefreshCw, TrendingUp, HandCoins,
  Receipt, Clock, Calculator, Search, CheckCircle2, Circle, ChevronDown,
  ChevronRight, Coins, PlusCircle, StickyNote, Percent, ShieldCheck, Undo2, SendHorizontal
} from 'lucide-react';

// Compte Assu : chiffre d'affaires, commission calculée, encaissements.
// Un dossier porte des LIGNES de CA (le CA initial puis les compléments).
// Chaque ligne a sa commission = (montant - retenue %) x part %, son statut payé et sa date.
// Exemple : 1000 € avec 30 % puis 15 % -> 700 € -> 105 €.

const NAVY = '#2d3f7f';
const VERT = '#2e9e5b';
const OCRE = '#b7791f';

const euro = (n) => (Number(n) || 0).toLocaleString('fr-FR', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2
});
const dateFr = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const aujourdhui = () => new Date().toISOString().slice(0, 10);

// Bascule tableaux -> cartes sous 860 px : un tableau à 10 colonnes est illisible sur téléphone.
function useMobile(seuil = 860) {
  const requete = `(max-width:${seuil}px)`;
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(requete).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(requete);
    const onChange = (e) => setMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    setMobile(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [requete]);
  return mobile;
}

// Étiquette + montant, brique de base des cartes mobiles
function Info({ label, valeur, couleur = '#2f3648', fort = false }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: '#57627d', letterSpacing: .3, marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: fort ? 17 : 15, fontWeight: fort ? 800 : 700, color: couleur }}>
        {valeur}
      </div>
    </div>
  );
}

const DOSSIER_VIDE = {
  client: '', reference: '', montant: '', taux_retenue: 30,
  taux_commission: 15, date_dossier: aujourdhui(), notes: '',
  paye: false, date_paiement: aujourdhui()
};

function CompteAssu({ currentUser }) {
  const [data, setData] = useState({ dossiers: [], mouvements: [], totaux: null });
  const [loading, setLoading] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [ouverts, setOuverts] = useState([]);
  const mobile = useMobile();
  // Le valideur consulte le compte de l'admin : aucune écriture, seulement la validation.
  const lectureSeule = (currentUser && currentUser.role === 'valideur') || data.lecture_seule === true;
  const estAdmin = currentUser && currentUser.role === 'admin';

  const [showDossier, setShowDossier] = useState(false);
  const [dossierEnCours, setDossierEnCours] = useState(null);
  const [formDossier, setFormDossier] = useState(DOSSIER_VIDE);

  const [complementPour, setComplementPour] = useState(null);
  const [formComplement, setFormComplement] = useState({
    montant: '', libelle: '', date_ca: aujourdhui(), paye: false, date_paiement: aujourdhui()
  });

  const [formEncaissement, setFormEncaissement] = useState({
    montant: '', taux_tva: 20, taux_commission: 15,
    libelle: '', date_encaissement: aujourdhui(), dossier_id: ''
  });

  const [formMouvement, setFormMouvement] = useState({
    acompte: { montant: '', libelle: '', date_mouvement: '', dossier_id: '' },
    avance_frais: { montant: '', libelle: '', date_mouvement: '', dossier_id: '' }
  });

  const API_URL = process.env.REACT_APP_API_URL || '';
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/compte-assu`, auth);
      setData(res.data);
    } catch (e) {
      console.error('Erreur chargement compte assu:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const calcul = (montant, retenue, part) => {
    const m = Number(montant) || 0;
    const apres = m * (1 - (Number(retenue) || 0) / 100);
    return { apres, gain: apres * ((Number(part) || 0) / 100) };
  };

  const basculerOuvert = (id) => {
    setOuverts(o => o.includes(id) ? o.filter(x => x !== id) : [...o, id]);
  };

  // ---------- Dossiers ----------
  const ouvrirDossier = (d) => {
    if (d) {
      setDossierEnCours(d);
      setFormDossier({
        ...DOSSIER_VIDE,
        client: d.client || '', reference: d.reference || '',
        taux_retenue: d.taux_retenue, taux_commission: d.taux_commission,
        date_dossier: d.date_dossier ? String(d.date_dossier).slice(0, 10) : '',
        notes: d.notes || ''
      });
    } else {
      setDossierEnCours(null);
      setFormDossier(DOSSIER_VIDE);
    }
    setShowDossier(true);
  };

  const enregistrerDossier = async (e) => {
    e.preventDefault();
    try {
      const res = dossierEnCours
        ? await axios.put(`${API_URL}/api/compte-assu/dossiers/${dossierEnCours.id}`, formDossier, auth)
        : await axios.post(`${API_URL}/api/compte-assu/dossiers`, formDossier, auth);
      setData(res.data);
      setShowDossier(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const supprimerDossier = async (d) => {
    if (!window.confirm(`Supprimer le dossier de ${d.client} et toutes ses lignes de chiffre d'affaires ?\n(Les acomptes et avances rattachés sont conservés, simplement détachés.)`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/compte-assu/dossiers/${d.id}`, auth);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // ---------- Lignes de chiffre d'affaires ----------
  const ouvrirComplement = (d) => {
    setComplementPour(d);
    setFormComplement({ montant: '', libelle: '', date_ca: aujourdhui(), paye: false, date_paiement: aujourdhui() });
  };

  const ajouterComplement = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(
        `${API_URL}/api/compte-assu/dossiers/${complementPour.id}/ca`, formComplement, auth);
      setData(res.data);
      setOuverts(o => o.includes(complementPour.id) ? o : [...o, complementPour.id]);
      setComplementPour(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'ajout');
    }
  };

  const payerDossier = async (d) => {
    try {
      const res = await axios.patch(`${API_URL}/api/compte-assu/dossiers/${d.id}/paiement`,
        { paye: true, date_paiement: aujourdhui() }, auth);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const basculerPaiement = async (ligne) => {
    try {
      const res = await axios.patch(`${API_URL}/api/compte-assu/ca/${ligne.id}/paiement`,
        { paye: !ligne.paye, date_paiement: aujourdhui() }, auth);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const supprimerLigne = async (ligne) => {
    if (!window.confirm(`Supprimer cette ligne de ${euro(ligne.montant)} ?`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/compte-assu/ca/${ligne.id}`, auth);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // ---------- Encaissements : TVA, commission, reste en acompte ----------
  const ventiler = (montant, tva, com) => {
    const brut = Number(montant) || 0;
    const mtTva = brut * ((Number(tva) || 0) / 100);
    const ht = brut - mtTva;
    const commission = ht * ((Number(com) || 0) / 100);
    return { brut, tva: mtTva, ht, commission, acompte: ht - commission };
  };

  const ajouterEncaissement = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/compte-assu/encaissements`, formEncaissement, auth);
      setData(res.data);
      setFormEncaissement({
        montant: '', taux_tva: formEncaissement.taux_tva, taux_commission: formEncaissement.taux_commission,
        libelle: '', date_encaissement: aujourdhui(), dossier_id: ''
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'ajout');
    }
  };

  const supprimerEncaissement = async (enc) => {
    if (!window.confirm(`Supprimer cet encaissement de ${euro(enc.montant)} ?`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/compte-assu/encaissements/${enc.id}`, auth);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // ---------- Acomptes et avances ----------
  const ajouterMouvement = async (type, e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/compte-assu/mouvements`,
        { ...formMouvement[type], type }, auth);
      setData(res.data);
      setFormMouvement({ ...formMouvement, [type]: { montant: '', libelle: '', date_mouvement: '', dossier_id: '' } });
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'ajout');
    }
  };

  const supprimerMouvement = async (m) => {
    if (!window.confirm(`Supprimer cette ligne de ${euro(m.montant)} ?`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/compte-assu/mouvements/${m.id}`, auth);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // Détail d'un dossier : commentaire puis lignes de CA. Utilisé par le tableau ET par les cartes mobiles.
  const detailDossier = (d) => (
    <>
                      {/* Commentaire du dossier, visible dès l'ouverture du détail */}
                      <div style={{
                        border: '1px solid #e6eaf3', borderRadius: 11, background: '#fff',
                        padding: '12px 14px', marginBottom: 10,
                        display: 'flex', gap: 11, alignItems: 'flex-start'
                      }}>
                        <StickyNote size={16} style={{ color: OCRE, flex: '0 0 auto', marginTop: 2 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11.5, fontWeight: 800, color: '#57627d', letterSpacing: .3, marginBottom: 4 }}>
                            COMMENTAIRE
                          </div>
                          {d.notes ? (
                            <div style={{ fontSize: 13.5, color: '#2f3648', whiteSpace: 'pre-wrap' }}>{d.notes}</div>
                          ) : (
                            <div style={{ fontSize: 13, color: '#5a6480', fontStyle: 'italic' }}>
                              Aucun commentaire — ajoutez-en un avec le crayon ✎ de la ligne.
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ border: '1px solid #e6eaf3', borderRadius: 11, overflow: 'hidden', background: '#fff' }}>
                        <div style={{ padding: '9px 14px', background: '#f7f8fb', fontSize: 11.5, fontWeight: 800, color: '#57627d', letterSpacing: .3 }}>
                          LIGNES DE CHIFFRE D'AFFAIRES
                        </div>
                        {d.lignes.length === 0 ? (
                          <div style={{ padding: 18, color: '#5a6480', fontSize: 13 }}>Aucune ligne.</div>
                        ) : d.lignes.map(l => (
                          <div key={l.id} style={{
                            display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                            borderTop: '1px solid #f4f4f4', flexWrap: 'wrap'
                          }}>
                            <div style={{ flex: '1 1 190px', minWidth: 0 }}>
                              <div style={{ fontWeight: 700, color: '#333' }}>
                                {euro(l.montant)}
                                <span style={{ fontWeight: 400, color: '#5a6480', fontSize: 12.5 }}>
                                  {' → '}commission <strong style={{ color: NAVY }}>{euro(l.gain)}</strong>
                                </span>
                              </div>
                              <div style={{ fontSize: 11.5, color: '#5a6480' }}>
                                {l.libelle || 'Ligne de CA'} · {dateFr(l.date_ca)}
                              </div>
                            </div>
                            <div style={{ flex: '0 0 auto' }}>
                              {l.paye ? (
                                <span style={{ ...puce, background: '#e6f5ec', color: VERT }}>
                                  <CheckCircle2 size={13} /> Payé le {dateFr(l.date_paiement)}
                                </span>
                              ) : (
                                <span style={{ ...puce, background: '#fdf6e8', color: OCRE }}>
                                  <Circle size={13} /> En attente
                                </span>
                              )}
                            </div>
                            <button onClick={() => basculerPaiement(l)}
                              style={{
                                padding: '7px 13px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                fontSize: 12.5, fontWeight: 700, flex: '0 0 auto',
                                background: l.paye ? '#f0f0f0' : VERT, color: l.paye ? '#666' : '#fff'
                              }}>
                              {l.paye ? 'Annuler le paiement' : "Marquer payé aujourd'hui"}
                            </button>
                            <button onClick={() => supprimerLigne(l)} title="Supprimer la ligne"
                              style={{ ...btnIcone, color: '#c62828', flex: '0 0 auto' }}><Trash2 size={14} /></button>
                          </div>
                        ))}
                        <div style={{ padding: '11px 14px', borderTop: '1px solid #f0f0f0', background: '#fbfcfe' }}>
                          <button onClick={() => ouvrirComplement(d)} style={{
                            padding: '8px 15px', background: '#fff', border: `1.5px solid ${VERT}`,
                            color: VERT, borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                            display: 'flex', alignItems: 'center', gap: 6
                          }}>
                            <Plus size={14} /> Ajouter un complément de CA
                          </button>
                        </div>
                      </div>
    </>
  );

  // ---------- Validation des comptes ----------
  const validerLigne = async (type, item, libelle) => {
    const ok = window.confirm(
      `Vous êtes sur le point de valider un compte.\n\n${libelle}\n\n` +
      `La validation est DÉFINITIVE : vous ne pourrez pas revenir dessus. ` +
      `Seul un administrateur peut l'annuler.\n\nConfirmer la validation ?`
    );
    if (!ok) return;
    try {
      const res = await axios.patch(`${API_URL}/api/compte-assu/valider`, { type, id: item.id }, auth);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la validation');
    }
  };

  const demanderValidation = async (type, item, libelle, demande = true) => {
    if (demande && !window.confirm(
      `Soumettre ce compte à la validation ?\n\n${libelle}\n\n` +
      `Il apparaîtra alors chez le valideur, qui pourra le valider définitivement.`
    )) return;
    try {
      const res = await axios.patch(`${API_URL}/api/compte-assu/demander-validation`,
        { type, id: item.id, demande }, auth);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const devaliderLigne = async (type, item) => {
    if (!window.confirm(`Annuler la validation faite par ${item.valide_par_nom || 'le valideur'} ?`)) return;
    try {
      const res = await axios.patch(`${API_URL}/api/compte-assu/devalider`, { type, id: item.id }, auth);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  // Trois états : à soumettre -> en attente -> validé. L'affichage dépend du rôle.
  const Validation = ({ type, item, libelle }) => {
    // Validé : tout le monde voit par qui et quand ; seul l'admin peut lever la validation
    if (item.valide_par) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ ...puce, background: '#eef2fb', color: NAVY }}>
            <ShieldCheck size={13} /> Validé par {item.valide_par_nom || '—'}
            {item.valide_le ? ` le ${dateFr(item.valide_le)}` : ''}
          </span>
          {estAdmin && (
            <button onClick={() => devaliderLigne(type, item)} title="Annuler la validation"
              style={{ ...btnIcone, color: '#c62828', padding: 4 }}><Undo2 size={14} /></button>
          )}
        </span>
      );
    }

    // Côté valideur : il ne reçoit que les lignes soumises, il peut donc valider
    if (lectureSeule) {
      return (
        <button onClick={() => validerLigne(type, item, libelle)} style={{
          padding: '8px 14px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8,
          cursor: 'pointer', fontSize: 13, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', gap: 6
        }}>
          <ShieldCheck size={15} /> Valider le compte
        </button>
      );
    }

    // Côté admin : soumettre, ou suivre l'attente
    if (item.demande_validation) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ ...puce, background: '#fdf6e8', color: OCRE }}>
            <Clock size={13} /> En attente de validation
          </span>
          <button onClick={() => demanderValidation(type, item, libelle, false)}
            title="Retirer la demande" style={{ ...btnIcone, color: '#5a6480', padding: 4 }}>
            <Undo2 size={14} />
          </button>
        </span>
      );
    }

    return (
      <button onClick={() => demanderValidation(type, item, libelle)} style={{
        padding: '7px 13px', background: '#fff', border: `1.5px solid ${NAVY}`, color: NAVY,
        borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', gap: 6
      }}>
        <SendHorizontal size={14} /> Demander validation
      </button>
    );
  };

  // ---------- Rendu ----------
  const t = data.totaux || {
    ca_total: 0, gain_total: 0, gain_paye: 0, gain_attente: 0,
    total_acomptes: 0, total_avances: 0, reste: 0, nb_dossiers: 0, nb_lignes: 0
  };

  const dossiersFiltres = data.dossiers.filter(d => {
    if (!recherche) return true;
    const q = recherche.toLowerCase();
    return String(d.client || '').toLowerCase().includes(q)
      || String(d.reference || '').toLowerCase().includes(q);
  });

  const nomDossier = (id) => {
    const d = data.dossiers.find(x => Number(x.id) === Number(id));
    return d ? d.client : null;
  };

  const cartes = [
    { label: "CHIFFRE D'AFFAIRES", valeur: t.ca_total, couleur: '#4a5674', icone: Coins,
      detail: `${t.nb_dossiers} dossier${t.nb_dossiers > 1 ? 's' : ''} · ${t.nb_lignes} ligne${t.nb_lignes > 1 ? 's' : ''}` },
    { label: 'COMMISSION TOTALE', valeur: t.gain_total, couleur: NAVY, icone: TrendingUp,
      detail: 'ce que le CA vous rapporte' },
    { label: 'ENCAISSÉ', valeur: t.gain_paye, couleur: VERT, icone: CheckCircle2,
      detail: 'lignes marquées payées' },
    { label: 'EN ATTENTE', valeur: t.gain_attente, couleur: OCRE, icone: Clock,
      detail: 'lignes pas encore payées' },
    { label: 'RESTE À PERCEVOIR', valeur: t.reste, couleur: t.reste < 0 ? '#c62828' : '#7e57c2', icone: Wallet,
      detail: t.reste < 0 ? 'trop-perçu' : 'attente − acomptes − acompte Eshops + frais avancés' }
  ];

  const listes = [
    { type: 'acompte', titre: 'Acomptes', couleur: VERT, icone: HandCoins, signe: '−',
      sens: 'déjà reçu — déduit de ce qui reste à encaisser' },
    { type: 'avance_frais', titre: 'Avances de frais', couleur: OCRE, icone: Receipt, signe: '+',
      sens: 'avancé pour le client — ajouté à ce qu\'il vous doit' }
  ];

  const encaissements = data.encaissements || [];
  const te = data.totaux_encaissements || { brut: 0, tva: 0, ht: 0, commission: 0, acompte: 0, nb: 0 };
  const ve = ventiler(formEncaissement.montant, formEncaissement.taux_tva, formEncaissement.taux_commission);

  const apercuDossier = calcul(formDossier.montant, formDossier.taux_retenue, formDossier.taux_commission);
  const apercuComp = complementPour
    ? calcul(formComplement.montant, complementPour.taux_retenue, complementPour.taux_commission)
    : { apres: 0, gain: 0 };

  return (
    <div style={{ padding: 20 }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, color: '#333', fontSize: 24, display: 'flex', alignItems: 'center', gap: 9 }}>
            <Wallet size={24} style={{ color: NAVY }} /> Compte Assu
          </h2>
          <p style={{ margin: '5px 0 0', color: '#666', fontSize: 14 }}>
            Chiffre d'affaires, commission calculée en direct, encaissements et reste à percevoir
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={charger} style={btnSecondaire}><RefreshCw size={15} /> Actualiser</button>
          {!lectureSeule && (
            <button onClick={() => ouvrirDossier(null)} style={btnPrincipal}><Plus size={15} /> Nouveau chiffre d'affaires</button>
          )}
        </div>
      </div>

      {/* Totaux */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 13, marginBottom: 20 }}>
        {cartes.map(c => {
          const Icone = c.icone;
          return (
            <div key={c.label} style={{
              background: '#fff', border: `2px solid ${c.couleur}22`, borderRadius: 14,
              padding: '16px 18px', borderLeft: `5px solid ${c.couleur}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                <Icone size={15} style={{ color: c.couleur }} />
                <span style={{ fontSize: 10.5, fontWeight: 800, color: '#57627d', letterSpacing: .4 }}>{c.label}</span>
              </div>
              <div style={{ fontSize: 23, fontWeight: 800, color: c.couleur, lineHeight: 1.1 }}>{euro(c.valeur)}</div>
              <div style={{ fontSize: 11, color: '#5a6480', marginTop: 4 }}>{c.detail}</div>
            </div>
          );
        })}
      </div>

      {/* Rappel du calcul */}
      <div style={{
        background: '#eef2fb', border: '1px solid #dbe3f5', borderRadius: 12,
        padding: '12px 16px', marginBottom: 20, fontSize: 13.5, color: NAVY,
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
      }}>
        <Calculator size={17} style={{ flex: '0 0 auto' }} />
        <span>
          <strong>Calcul :</strong> on retire la retenue du chiffre d'affaires, puis vous touchez votre part
          sur ce qui reste. 1 000 € − 30 % = 700 €, puis 15 % = <strong>105 €</strong>.
          Un complément de 500 € ajoute <strong>52,50 €</strong>. Les deux taux sont modifiables dossier par dossier.
        </span>
      </div>

      {/* Dossiers */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '15px 18px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <strong style={{ color: NAVY, fontSize: 16 }}>Mes dossiers</strong>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: 10, color: '#7a839b' }} />
            <input type="text" placeholder="Rechercher un client…" value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              style={{ padding: '8px 12px 8px 34px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 13.5, width: 230 }} />
          </div>
        </div>
        {!mobile && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: '#f7f8fb' }}>
                <th style={{ ...th, width: 34 }} />
                <th style={th}>Client</th>
                <th style={thNum}>CA</th>
                <th style={thNum}>Commission</th>
                <th style={thNum}>Encaissé</th>
                <th style={thNum}>En attente</th>
                <th style={thNum}>Reçu</th>
                <th style={thNum}>Frais avancés</th>
                <th style={thNum}>Reste dû</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={vide}>Chargement…</td></tr>
              ) : dossiersFiltres.length === 0 ? (
                <tr><td colSpan={10} style={vide}>
                  {data.dossiers.length === 0
                    ? "Aucun dossier. Cliquez sur « Nouveau chiffre d'affaires »."
                    : 'Aucun dossier ne correspond à cette recherche.'}
                </td></tr>
              ) : dossiersFiltres.map(d => {
                const ouvert = ouverts.includes(d.id);
                return (
                  <React.Fragment key={d.id}>
                    <tr style={{ borderTop: '1px solid #f2f2f2', background: ouvert ? '#fbfcfe' : '#fff' }}>
                      <td style={{ ...td, paddingRight: 0 }}>
                        <button onClick={() => basculerOuvert(d.id)} title="Voir les lignes" style={btnIcone}>
                          {ouvert ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                        </button>
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: '#333' }}>{d.client}</div>
                        <div style={{ fontSize: 11.5, color: '#5a6480' }}>
                          {d.reference ? `${d.reference} · ` : ''}{dateFr(d.date_dossier)}
                          {` · ${d.taux_retenue}% / ${d.taux_commission}%`}
                          {` · ${d.lignes.length} ligne${d.lignes.length > 1 ? 's' : ''}`}
                        </div>
                      </td>
                      <td style={tdNum}>{euro(d.montant)}</td>
                      <td style={{ ...tdNum, fontWeight: 800, color: NAVY }}>{euro(d.gain)}</td>
                      <td style={{ ...tdNum, color: VERT }}>{euro(d.gain_paye)}</td>
                      <td style={{ ...tdNum, color: OCRE }}>{euro(d.gain_attente)}</td>
                      <td style={{ ...tdNum, color: VERT }}>
                        {d.acomptes_recus + d.acompte_eshops > 0 ? '− ' : ''}{euro(d.acomptes_recus + d.acompte_eshops)}
                      </td>
                      <td style={{ ...tdNum, color: OCRE }}>
                        {d.avances_recues > 0 ? '+ ' : ''}{euro(d.avances_recues)}
                      </td>
                      <td style={{ ...tdNum, fontWeight: 800, color: d.reste_du > 0 ? '#c62828' : VERT }}>{euro(d.reste_du)}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Validation type="dossier" item={d} libelle={`Dossier ${d.client} — commission ${euro(d.gain)}`} />
                        {!lectureSeule && d.gain_attente > 0 ? (
                          <button onClick={() => payerDossier(d)}
                            title={`Marquer payé aujourd'hui (${euro(d.gain_attente)})`}
                            style={{
                              padding: '6px 12px', marginRight: 6, background: VERT, color: '#fff',
                              border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12.5,
                              fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5,
                              verticalAlign: 'middle'
                            }}>
                            <CheckCircle2 size={14} /> Payé
                          </button>
                        ) : d.lignes.length > 0 ? (
                          <span style={{
                            ...puce, background: '#e6f5ec', color: VERT, marginRight: 6, verticalAlign: 'middle'
                          }}>
                            <CheckCircle2 size={12} /> Soldé
                          </span>
                        ) : null}
                        {!lectureSeule && (<>
                          <button onClick={() => ouvrirComplement(d)} title="Ajouter un complément de CA"
                            style={{ ...btnIcone, color: VERT }}><PlusCircle size={17} /></button>
                          <button onClick={() => ouvrirDossier(d)} title="Modifier" style={btnIcone}><Pencil size={15} /></button>
                          <button onClick={() => supprimerDossier(d)} title="Supprimer" style={{ ...btnIcone, color: '#c62828' }}><Trash2 size={15} /></button>
                        </>)}
                      </td>
                    </tr>

                    {ouvert && (
                      <tr>
                        <td colSpan={10} style={{ padding: '0 18px 16px 52px', background: '#fbfcfe' }}>
                          {detailDossier(d)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            {dossiersFiltres.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f7f8fb', borderTop: '2px solid #e6eaf3' }}>
                  <td style={td} />
                  <td style={{ ...td, fontWeight: 800, color: NAVY }}>Total</td>
                  <td style={{ ...tdNum, fontWeight: 700 }}>{euro(dossiersFiltres.reduce((s, d) => s + d.montant, 0))}</td>
                  <td style={{ ...tdNum, fontWeight: 800, color: NAVY }}>{euro(dossiersFiltres.reduce((s, d) => s + d.gain, 0))}</td>
                  <td style={{ ...tdNum, fontWeight: 700, color: VERT }}>{euro(dossiersFiltres.reduce((s, d) => s + d.gain_paye, 0))}</td>
                  <td style={{ ...tdNum, fontWeight: 700, color: OCRE }}>{euro(dossiersFiltres.reduce((s, d) => s + d.gain_attente, 0))}</td>
                  <td style={{ ...tdNum, fontWeight: 700, color: VERT }}>− {euro(dossiersFiltres.reduce((s, d) => s + d.acomptes_recus + d.acompte_eshops, 0))}</td>
                  <td style={{ ...tdNum, fontWeight: 700, color: OCRE }}>+ {euro(dossiersFiltres.reduce((s, d) => s + d.avances_recues, 0))}</td>
                  <td style={{ ...tdNum, fontWeight: 800, color: '#c62828' }}>{euro(dossiersFiltres.reduce((s, d) => s + d.reste_du, 0))}</td>
                  <td style={td} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        )}

        {/* Sur mobile : une carte par dossier */}
        {mobile && (
          <div style={{ padding: 14 }}>
            {loading ? (
              <div style={vide}>Chargement…</div>
            ) : dossiersFiltres.length === 0 ? (
              <div style={vide}>
                {data.dossiers.length === 0
                  ? "Aucun dossier. Touchez « Nouveau chiffre d'affaires »."
                  : 'Aucun dossier ne correspond à cette recherche.'}
              </div>
            ) : dossiersFiltres.map(d => {
              const ouvert = ouverts.includes(d.id);
              return (
                <div key={d.id} style={carteMobile}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, color: '#2f3648', fontSize: 16 }}>{d.client}</div>
                      <div style={{ fontSize: 11.5, color: '#5a6480', marginTop: 2 }}>
                        {d.reference ? `${d.reference} · ` : ''}{dateFr(d.date_dossier)}
                        {` · ${d.taux_retenue}% / ${d.taux_commission}%`}
                      </div>
                    </div>
                    <button onClick={() => basculerOuvert(d.id)} style={{ ...btnIcone, flex: '0 0 auto' }}>
                      {ouvert ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '14px 0 4px' }}>
                    <Info label="CHIFFRE D'AFFAIRES" valeur={euro(d.montant)} />
                    <Info label="COMMISSION" valeur={euro(d.gain)} couleur={NAVY} fort />
                    <Info label="ENCAISSÉ" valeur={euro(d.gain_paye)} couleur={VERT} />
                    <Info label="EN ATTENTE" valeur={euro(d.gain_attente)} couleur={OCRE} />
                  </div>

                  <div style={{
                    borderTop: '1px dashed #e6eaf3', marginTop: 12, paddingTop: 12,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10
                  }}>
                    <Info label="RESTE DÛ" valeur={euro(d.reste_du)}
                      couleur={d.reste_du > 0 ? '#c62828' : VERT} fort />
                    {!lectureSeule && d.gain_attente > 0 ? (
                      <button onClick={() => payerDossier(d)} style={{
                        padding: '10px 16px', background: VERT, color: '#fff', border: 'none',
                        borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 700,
                        display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto'
                      }}><CheckCircle2 size={16} /> Payé</button>
                    ) : d.lignes.length > 0 ? (
                      <span style={{ ...puce, background: '#e6f5ec', color: VERT }}>
                        <CheckCircle2 size={13} /> Soldé
                      </span>
                    ) : null}
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <Validation type="dossier" item={d} libelle={`Dossier ${d.client} — commission ${euro(d.gain)}`} />
                  </div>
                  {!lectureSeule && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <button onClick={() => ouvrirComplement(d)} style={btnMobileSecondaire(VERT)}>
                      <Plus size={15} /> Complément
                    </button>
                    <button onClick={() => ouvrirDossier(d)} style={btnMobileSecondaire(NAVY)}>
                      <Pencil size={14} /> Modifier
                    </button>
                    <button onClick={() => supprimerDossier(d)} style={btnMobileSecondaire('#c62828')}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  )}

                  {ouvert && <div style={{ marginTop: 14 }}>{detailDossier(d)}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Encaissement Eshops : TVA retirée, puis commission, le reste en acompte */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '15px 18px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <strong style={{ color: NAVY, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Percent size={17} /> Encaissement Eshops
            </strong>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13 }}>
              <span style={{ color: '#57627d' }}>Brut <strong style={{ color: '#2f3648' }}>{euro(te.brut)}</strong></span>
              <span style={{ color: '#57627d' }}>TVA <strong style={{ color: '#7e57c2' }}>{euro(te.tva)}</strong></span>
              <span style={{ color: '#57627d' }}>Ma commission <strong style={{ color: NAVY }}>{euro(te.commission)}</strong></span>
              <span style={{ color: '#57627d' }}>Acompte <strong style={{ color: VERT }}>{euro(te.acompte)}</strong></span>
            </div>
          </div>
          <div style={{ fontSize: 12.5, color: '#57627d', marginTop: 7 }}>
            On retire la TVA, puis ma commission sur le HT ; le reste part en acompte.
            Exemple : 1 000 € − 20 % = 800 € → 15 % = 120 € pour moi → <strong>680 € en acompte</strong>.
          </div>
        </div>

        {/* Saisie */}
        {!lectureSeule && (
        <form onSubmit={ajouterEncaissement} style={{ padding: '14px 18px', background: '#fbfcfe', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ width: 130, flex: mobile ? '1 1 45%' : undefined }}>
              <label style={label}>Montant €</label>
              <input type="number" step="0.01" min="0.01" required placeholder="1000" value={formEncaissement.montant}
                onChange={(e) => setFormEncaissement({ ...formEncaissement, montant: e.target.value })} style={champ} />
            </div>
            <div style={{ width: 90, flex: mobile ? '1 1 45%' : undefined }}>
              <label style={label}>TVA %</label>
              <input type="number" step="0.01" min="0" max="100" value={formEncaissement.taux_tva}
                onChange={(e) => setFormEncaissement({ ...formEncaissement, taux_tva: e.target.value })} style={champ} />
            </div>
            <div style={{ width: 100, flex: mobile ? '1 1 45%' : undefined }}>
              <label style={label}>Ma part %</label>
              <input type="number" step="0.01" min="0" max="100" value={formEncaissement.taux_commission}
                onChange={(e) => setFormEncaissement({ ...formEncaissement, taux_commission: e.target.value })} style={champ} />
            </div>
            <div style={{ width: 145, flex: mobile ? '1 1 45%' : undefined }}>
              <label style={label}>Date</label>
              <input type="date" value={formEncaissement.date_encaissement}
                onChange={(e) => setFormEncaissement({ ...formEncaissement, date_encaissement: e.target.value })} style={champ} />
            </div>
            <div style={{ flex: '1 1 160px', minWidth: 140 }}>
              <label style={label}>Libellé</label>
              <input type="text" placeholder="Facultatif" value={formEncaissement.libelle}
                onChange={(e) => setFormEncaissement({ ...formEncaissement, libelle: e.target.value })} style={champ} />
            </div>
            <button type="submit" style={{ ...btnPrincipal, height: 36 }}><Plus size={15} /> Ajouter</button>
          </div>

          {/* Ventilation calculée pendant la saisie */}
          <div style={{ ...apercuBoite, marginTop: 12, marginBottom: 0 }}>
            <Calculator size={18} style={{ color: NAVY }} />
            <span style={{ fontSize: 14, color: '#4a5674' }}>
              {euro(ve.brut)} − TVA {formEncaissement.taux_tva || 0} % ({euro(ve.tva)}) ={' '}
              <strong>{euro(ve.ht)}</strong> → ma part {formEncaissement.taux_commission || 0} % ={' '}
              <strong style={{ color: NAVY }}>{euro(ve.commission)}</strong> → reste en acompte
            </span>
            <span style={{ fontSize: 22, fontWeight: 800, color: VERT }}>{euro(ve.acompte)}</span>
          </div>
        </form>
        )}

        {/* Tableau (ordinateur) */}
        {!mobile && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: '#f7f8fb' }}>
                <th style={th}>Date / libellé</th>
                <th style={thNum}>Brut</th>
                <th style={thNum}>TVA</th>
                <th style={thNum}>HT</th>
                <th style={thNum}>Ma commission</th>
                <th style={thNum}>Acompte</th>
                <th style={{ ...th, textAlign: 'right' }} />
              </tr>
            </thead>
            <tbody>
              {encaissements.length === 0 ? (
                <tr><td colSpan={7} style={vide}>Aucun encaissement pour le moment.</td></tr>
              ) : encaissements.map(enc => (
                <tr key={enc.id} style={{ borderTop: '1px solid #f2f2f2' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 700 }}>{dateFr(enc.date_encaissement)}</div>
                    <div style={{ fontSize: 11.5, color: '#5a6480' }}>
                      {enc.libelle || 'Encaissement'}
                      {enc.dossier_id && nomDossier(enc.dossier_id) ? ` · ${nomDossier(enc.dossier_id)}` : ''}
                      {` · TVA ${enc.taux_tva}% / part ${enc.taux_commission}%`}
                    </div>
                  </td>
                  <td style={{ ...tdNum, fontWeight: 700 }}>{euro(enc.montant)}</td>
                  <td style={{ ...tdNum, color: '#7e57c2' }}>− {euro(enc.tva)}</td>
                  <td style={{ ...tdNum, color: '#57627d' }}>{euro(enc.ht)}</td>
                  <td style={{ ...tdNum, fontWeight: 800, color: NAVY }}>{euro(enc.commission)}</td>
                  <td style={{ ...tdNum, fontWeight: 800, color: VERT }}>{euro(enc.acompte)}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Validation type="encaissement" item={enc}
                      libelle={`Encaissement ${euro(enc.montant)} du ${dateFr(enc.date_encaissement)}`} />
                    {!lectureSeule && (
                      <button onClick={() => supprimerEncaissement(enc)} title="Supprimer"
                        style={{ ...btnIcone, color: '#c62828' }}><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {encaissements.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f7f8fb', borderTop: '2px solid #e6eaf3' }}>
                  <td style={{ ...td, fontWeight: 800, color: NAVY }}>Total ({te.nb})</td>
                  <td style={{ ...tdNum, fontWeight: 800 }}>{euro(te.brut)}</td>
                  <td style={{ ...tdNum, fontWeight: 700, color: '#7e57c2' }}>− {euro(te.tva)}</td>
                  <td style={{ ...tdNum, fontWeight: 700, color: '#57627d' }}>{euro(te.ht)}</td>
                  <td style={{ ...tdNum, fontWeight: 800, color: NAVY }}>{euro(te.commission)}</td>
                  <td style={{ ...tdNum, fontWeight: 800, color: VERT }}>{euro(te.acompte)}</td>
                  <td style={td} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        )}

        {/* Sur mobile : une carte par encaissement */}
        {mobile && (
          <div style={{ padding: 14 }}>
            {encaissements.length === 0 ? (
              <div style={vide}>Aucun encaissement pour le moment.</div>
            ) : encaissements.map(enc => (
              <div key={enc.id} style={carteMobile}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, color: '#2f3648', fontSize: 15 }}>
                      {euro(enc.montant)} <span style={{ fontWeight: 600, fontSize: 12.5, color: '#5a6480' }}>brut</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: '#5a6480', marginTop: 2 }}>
                      {dateFr(enc.date_encaissement)} · {enc.libelle || 'Encaissement'}
                      {` · TVA ${enc.taux_tva}% / part ${enc.taux_commission}%`}
                    </div>
                  </div>
                  {!lectureSeule && (
                    <button onClick={() => supprimerEncaissement(enc)}
                      style={{ ...btnIcone, color: '#c62828', flex: '0 0 auto' }}><Trash2 size={16} /></button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 13 }}>
                  <Info label="TVA" valeur={`− ${euro(enc.tva)}`} couleur="#7e57c2" />
                  <Info label="HT" valeur={euro(enc.ht)} couleur="#57627d" />
                  <Info label="MA COMMISSION" valeur={euro(enc.commission)} couleur={NAVY} fort />
                  <Info label="ACOMPTE" valeur={euro(enc.acompte)} couleur={VERT} fort />
                </div>
                <div style={{ marginTop: 12 }}>
                  <Validation type="encaissement" item={enc}
                    libelle={`Encaissement ${euro(enc.montant)} du ${dateFr(enc.date_encaissement)}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Acomptes et avances */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18 }}>
        {listes.map(l => {
          const Icone = l.icone;
          const lignes = data.mouvements.filter(m => m.type === l.type);
          const total = lignes.reduce((s, m) => s + Number(m.montant), 0);
          const f = formMouvement[l.type];
          return (
            <div key={l.type} style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: l.couleur }}>
                  <Icone size={17} /> {l.titre}
                </span>
                <span style={{ fontWeight: 800, color: l.couleur }}>{l.signe} {euro(total)}</span>
              </div>
              <div style={{ padding: '9px 18px 0', fontSize: 12, color: '#57627d' }}>{l.sens}</div>
              {!lectureSeule && (
              <form onSubmit={(e) => ajouterMouvement(l.type, e)} style={{ padding: '14px 18px', background: '#fbfcfe', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <input type="number" step="0.01" min="0.01" required placeholder="Montant €" value={f.montant}
                    onChange={(e) => setFormMouvement({ ...formMouvement, [l.type]: { ...f, montant: e.target.value } })}
                    style={{ ...champ, width: 120 }} />
                  <input type="date" value={f.date_mouvement}
                    onChange={(e) => setFormMouvement({ ...formMouvement, [l.type]: { ...f, date_mouvement: e.target.value } })}
                    style={{ ...champ, flex: 1, minWidth: 145 }} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" placeholder="Libellé (facultatif)" value={f.libelle}
                    onChange={(e) => setFormMouvement({ ...formMouvement, [l.type]: { ...f, libelle: e.target.value } })}
                    style={{ ...champ, flex: 1 }} />
                  <button type="submit" style={{
                    padding: '9px 16px', background: l.couleur, color: '#fff', border: 'none',
                    borderRadius: 8, cursor: 'pointer', fontSize: 13.5, fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 6
                  }}><Plus size={15} /> Ajouter</button>
                </div>
              </form>
              )}
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {lignes.length === 0 ? (
                  <div style={{ padding: 26, textAlign: 'center', color: '#5a6480', fontSize: 13.5 }}>Aucune ligne pour le moment.</div>
                ) : lignes.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', borderBottom: '1px solid #f5f5f5' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: '#333', fontSize: 14 }}>{euro(m.montant)}</div>
                      <div style={{ fontSize: 11.5, color: '#5a6480' }}>
                        {dateFr(m.date_mouvement)}
                        {m.dossier_id && nomDossier(m.dossier_id) ? ` · ${nomDossier(m.dossier_id)}` : ''}
                        {m.libelle ? ` · ${m.libelle}` : ''}
                      </div>
                    </div>
                    <Validation type="mouvement" item={m}
                      libelle={`${l.titre} de ${euro(m.montant)}`} />
                    {!lectureSeule && (
                      <button onClick={() => supprimerMouvement(m)} title="Supprimer" style={{ ...btnIcone, color: '#c62828' }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Fenêtre : nouveau chiffre d'affaires / modification */}
      {showDossier && (
        <div style={overlay} onClick={() => setShowDossier(false)}>
          <div style={modale} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, color: NAVY, fontSize: 19 }}>
                {dossierEnCours ? 'Modifier le dossier' : "Nouveau chiffre d'affaires"}
              </h3>
              <button onClick={() => setShowDossier(false)} style={{ ...btnIcone, color: '#5f6b85' }}><X size={20} /></button>
            </div>

            <form onSubmit={enregistrerDossier}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={label}>Client <span style={{ color: '#c62828' }}>*</span></label>
                  <input type="text" required value={formDossier.client}
                    onChange={(e) => setFormDossier({ ...formDossier, client: e.target.value })}
                    style={champ} placeholder="Nom du client" />
                </div>
                <div>
                  <label style={label}>Référence</label>
                  <input type="text" value={formDossier.reference}
                    onChange={(e) => setFormDossier({ ...formDossier, reference: e.target.value })}
                    style={champ} placeholder="N° de dossier" />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={label}>
                    {dossierEnCours ? 'CA (via les lignes)' : "Chiffre d'affaires €"}
                    {!dossierEnCours && <span style={{ color: '#c62828' }}> *</span>}
                  </label>
                  <input type="number" step="0.01" min="0" required={!dossierEnCours}
                    disabled={!!dossierEnCours}
                    value={dossierEnCours ? dossierEnCours.montant : formDossier.montant}
                    onChange={(e) => setFormDossier({ ...formDossier, montant: e.target.value })}
                    style={{ ...champ, background: dossierEnCours ? '#f4f5f8' : '#fff' }} placeholder="1000" />
                </div>
                <div>
                  <label style={label}>Retenue %</label>
                  <input type="number" step="0.01" min="0" max="100" value={formDossier.taux_retenue}
                    onChange={(e) => setFormDossier({ ...formDossier, taux_retenue: e.target.value })} style={champ} />
                </div>
                <div>
                  <label style={label}>Ma part %</label>
                  <input type="number" step="0.01" min="0" max="100" value={formDossier.taux_commission}
                    onChange={(e) => setFormDossier({ ...formDossier, taux_commission: e.target.value })} style={champ} />
                </div>
                <div>
                  <label style={label}>Date</label>
                  <input type="date" value={formDossier.date_dossier}
                    onChange={(e) => setFormDossier({ ...formDossier, date_dossier: e.target.value })} style={champ} />
                </div>
              </div>

              {!dossierEnCours && (
                <>
                  <div style={apercuBoite}>
                    <Calculator size={18} style={{ color: NAVY }} />
                    <span style={{ fontSize: 14, color: '#4a5674' }}>
                      {euro(Number(formDossier.montant) || 0)} − {formDossier.taux_retenue || 0} % ={' '}
                      <strong>{euro(apercuDossier.apres)}</strong> → {formDossier.taux_commission || 0} % =
                    </span>
                    <span style={{ fontSize: 22, fontWeight: 800, color: NAVY }}>{euro(apercuDossier.gain)}</span>
                  </div>

                  <div style={caseAPocher}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: VERT }}>
                      <input type="checkbox" checked={formDossier.paye}
                        onChange={(e) => setFormDossier({ ...formDossier, paye: e.target.checked })}
                        style={{ width: 17, height: 17, cursor: 'pointer' }} />
                      Cette commission est déjà payée
                    </label>
                    {formDossier.paye && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <span style={{ fontSize: 13, color: '#4a5674' }}>le</span>
                        <input type="date" value={formDossier.date_paiement}
                          onChange={(e) => setFormDossier({ ...formDossier, date_paiement: e.target.value })}
                          style={{ ...champ, width: 160 }} />
                      </div>
                    )}
                  </div>
                </>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={label}>Notes</label>
                <textarea rows={2} value={formDossier.notes}
                  onChange={(e) => setFormDossier({ ...formDossier, notes: e.target.value })}
                  style={{ ...champ, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              <button type="submit" style={{ ...btnPrincipal, width: '100%', justifyContent: 'center' }}>
                {dossierEnCours ? 'Enregistrer les modifications' : 'Créer le dossier'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Fenêtre : complément de CA */}
      {complementPour && (
        <div style={overlay} onClick={() => setComplementPour(null)}>
          <div style={{ ...modale, maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0, color: NAVY, fontSize: 19 }}>Ajouter un complément de CA</h3>
              <button onClick={() => setComplementPour(null)} style={{ ...btnIcone, color: '#5f6b85' }}><X size={20} /></button>
            </div>
            <p style={{ margin: '0 0 18px', color: '#5a6480', fontSize: 13.5 }}>
              {complementPour.client} · CA actuel {euro(complementPour.montant)} · commission {euro(complementPour.gain)}
            </p>

            <form onSubmit={ajouterComplement}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={label}>Montant du complément € <span style={{ color: '#c62828' }}>*</span></label>
                  <input type="number" step="0.01" min="0.01" required autoFocus value={formComplement.montant}
                    onChange={(e) => setFormComplement({ ...formComplement, montant: e.target.value })}
                    style={champ} placeholder="500" />
                </div>
                <div>
                  <label style={label}>Libellé</label>
                  <input type="text" value={formComplement.libelle}
                    onChange={(e) => setFormComplement({ ...formComplement, libelle: e.target.value })}
                    style={champ} placeholder="Complément" />
                </div>
                <div>
                  <label style={label}>Date</label>
                  <input type="date" value={formComplement.date_ca}
                    onChange={(e) => setFormComplement({ ...formComplement, date_ca: e.target.value })} style={champ} />
                </div>
              </div>

              {/* La différence, et le nouveau total */}
              <div style={{ ...apercuBoite, flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <Calculator size={18} style={{ color: NAVY }} />
                  <span style={{ fontSize: 14, color: '#4a5674' }}>
                    Différence : {euro(Number(formComplement.montant) || 0)} − {complementPour.taux_retenue} % ={' '}
                    <strong>{euro(apercuComp.apres)}</strong> → {complementPour.taux_commission} % =
                  </span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: VERT }}>+ {euro(apercuComp.gain)}</span>
                </div>
                <div style={{ borderTop: '1px solid #dbe3f5', paddingTop: 9, fontSize: 13.5, color: '#4a5674' }}>
                  Nouveau CA : <strong>{euro(complementPour.montant + (Number(formComplement.montant) || 0))}</strong>
                  {'  ·  '}Commission totale : <strong style={{ color: NAVY }}>{euro(complementPour.gain + apercuComp.gain)}</strong>
                </div>
              </div>

              <div style={caseAPocher}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: VERT }}>
                  <input type="checkbox" checked={formComplement.paye}
                    onChange={(e) => setFormComplement({ ...formComplement, paye: e.target.checked })}
                    style={{ width: 17, height: 17, cursor: 'pointer' }} />
                  Ce complément est déjà payé
                </label>
                {formComplement.paye && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <span style={{ fontSize: 13, color: '#4a5674' }}>le</span>
                    <input type="date" value={formComplement.date_paiement}
                      onChange={(e) => setFormComplement({ ...formComplement, date_paiement: e.target.value })}
                      style={{ ...champ, width: 160 }} />
                  </div>
                )}
              </div>

              <button type="submit" style={{ ...btnPrincipal, width: '100%', justifyContent: 'center', backgroundColor: VERT }}>
                Ajouter le complément
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: '12px 14px', textAlign: 'left', fontSize: 11.5, color: '#57627d', fontWeight: 800, letterSpacing: .3 };
const thNum = { ...th, textAlign: 'right' };
// Couleur explicite : sans elle, les cellules sans couleur propre (colonne CA)
// héritaient du gris clair ambiant de l'application et devenaient illisibles.
const td = { padding: '12px 14px', verticalAlign: 'middle', color: '#2f3648' };
const tdNum = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const vide = { padding: 36, textAlign: 'center', color: '#5a6480' };
const champ = { width: '100%', padding: '9px 11px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 13.5, boxSizing: 'border-box' };
const label = { display: 'block', fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 5 };
const carteMobile = {
  border: '1px solid #e6eaf3', borderRadius: 13, padding: '15px 16px',
  marginBottom: 12, background: '#fff', boxShadow: '0 2px 8px rgba(45,63,127,.05)'
};
const btnMobileSecondaire = (couleur) => ({
  flex: '1 1 auto', padding: '10px 12px', background: '#fff', border: `1.5px solid ${couleur}33`,
  color: couleur, borderRadius: 9, cursor: 'pointer', fontSize: 13.5, fontWeight: 700,
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
});
const puce = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700 };
const apercuBoite = {
  background: '#eef2fb', border: '1px solid #dbe3f5', borderRadius: 11,
  padding: '14px 16px', marginBottom: 14, display: 'flex',
  alignItems: 'center', gap: 12, flexWrap: 'wrap'
};
const caseAPocher = {
  background: '#f4fbf6', border: '1px solid #cfe9d8', borderRadius: 11,
  padding: '13px 16px', marginBottom: 16, display: 'flex',
  alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'
};
const btnPrincipal = {
  padding: '10px 18px', backgroundColor: NAVY, color: '#fff', border: 'none', borderRadius: 8,
  cursor: 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6
};
const btnSecondaire = { ...btnPrincipal, backgroundColor: '#fff', color: '#555', border: '1.5px solid #ddd' };
const btnIcone = {
  background: 'none', border: 'none', cursor: 'pointer', color: NAVY,
  padding: 6, display: 'inline-flex', alignItems: 'center'
};
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
  alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 20, overflowY: 'auto'
};
const modale = {
  background: '#fff', borderRadius: 14, padding: 26, maxWidth: 720, width: '100%',
  margin: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
};

export default CompteAssu;
